import React, { memo, useCallback, useEffect, useMemo, type MouseEvent } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { type PreloadedEntryPoint, graphql, useFragment } from 'react-relay';
import { parse, stringify } from 'query-string';
import { toProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import {
	getEditionForProject,
	type ApplicationEdition,
} from '@jira/platform__application-editions/src/edition.tsx';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Shortcuts from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcuts/index.tsx';

import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	SCREEN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import UFOLabel from '@atlassian/jira-ufo-label/src/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { RouterSubscriber } from '@atlassian/react-resource-router/src/controllers/router-subscriber/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import type { AnyEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import type { JiraProjectType } from '../__generated__/main_issueNavigator_IssueNavigatorUI_project.graphql';
import { ANALYTICS_SOURCE, ANALYTICS_LIST_VIEW_SOURCE_NAME } from '../../common/constants.tsx';
import IssueApp from '../../common/ui/issue-app/main.tsx';
import {
	useSelectedIssueKey,
	useSelectedIssueActions,
} from '../../controllers/selected-issue/hooks.tsx';
import { useInfiniteScroll } from '../../services/infinite-scroll/index.tsx';
import { useIsFetching } from '../../services/issue-search/selectors.tsx';
import { useFlatListIssuesActions } from '../../controllers/flat-list-issues/index.tsx';
import type { main_issueNavigator_FullPageIssueApp_view$key as ViewFragment } from './__generated__/main_issueNavigator_FullPageIssueApp_view.graphql';
import type { main_issueNavigator_FullPageIssueApp_issueResults$key as IssueResultsFragment } from './__generated__/main_issueNavigator_FullPageIssueApp_issueResults.graphql';
import messages from './messages.tsx';

type IssueKey = string;

type Props = {
	issueResults: IssueResultsFragment | null;
	view: ViewFragment | null;
	onChangeIssue: (issueKey: IssueKey, isSelectedByUserInteraction: boolean) => void;
	projectType?: JiraProjectType | null;
	routePathname: string;
	routeSearch: string;
	issueAppEntryPoint?: PreloadedEntryPoint<AnyEntryPoint>;
};

const useAnalyticsAttributes = (type: JiraProjectType | null | undefined) => {
	const appEditions = useAppEditions();

	return useMemo(() => {
		if (!type) return;

		const projectType = toProjectType(type.toLowerCase());
		if (!projectType) return;

		const edition = getEditionForProject(projectType, appEditions);

		if (!edition) return;

		return { edition };
	}, [appEditions, type]);
};

const FullPageIssueApp = ({
	onChangeIssue,
	issueResults,
	view,
	projectType,
	routePathname,
	routeSearch,
	issueAppEntryPoint,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const isFetching = useIsFetching();
	const { exitFullPageIssueAppMode, selectNextIssue, selectPreviousIssue } =
		useSelectedIssueActions();
	const { mergeFlatListIssues } = useFlatListIssuesActions();

	const onBackToSearch = useCallback(() => {
		// Clear the selected issue key from the URL and return to normal view mode
		onChangeIssue('', true);
		exitFullPageIssueAppMode();
	}, [exitFullPageIssueAppMode, onChangeIssue]);

	const onBackToSearchByButton = useCallback(
		(e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			// @ts-expect-error - Argument of type 'UIAnalyticsEvent | null' is not assignable to parameter of type 'UIAnalyticsEvent'.
			fireUIAnalytics(analyticsEvent.clone(), 'returnToSearch');
			onBackToSearch();
		},
		[onBackToSearch],
	);

	const onBackToSearchByKeyboardShortcut = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'toggled',
				actionSubject: 'issue',
			}),
			'returnToSearch',
			{
				keyPressed: 'z',
				keyboardShortcut: true,
			},
		);
		onBackToSearch();
	}, [onBackToSearch, createAnalyticsEvent]);

	const onNextIssue = useCallback(() => {
		if (selectNextIssue()) {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'nextIssue',
				{ keyboardShortcut: true },
			);
		}
	}, [createAnalyticsEvent, selectNextIssue]);

	const onPreviousIssue = useCallback(() => {
		if (selectPreviousIssue()) {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'previousIssue',
				{ keyboardShortcut: true },
			);
		}
	}, [createAnalyticsEvent, selectPreviousIssue]);

	// If a new search is being run and we do not have forced issue selection (i.e. issue app pagination) then return to
	// normal view mode. This can happen when a filter is clicked in the sidebar or browser history navigation.
	useEffect(() => {
		if (isFetching) {
			exitFullPageIssueAppMode();
		}
	}, [exitFullPageIssueAppMode, isFetching]);

	const backToSearch = (
		<Button
			testId="issue-navigator.ui.full-page-issue-app.return-to-search"
			onClick={onBackToSearchByButton}
			interactionName="issue-navigator-full-page-issue-app-return-to-search"
		>
			{formatMessage(messages.returnText)}
		</Button>
	);

	const issueResultsData = useFragment<IssueResultsFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_FullPageIssueApp_issueResults on JiraIssueConnection {
				edges {
					node {
						key
					}
				}
				...main_issueNavigator_IssueAppWithData_issueResults
			}
		`,
		issueResults,
	);

	const viewData = useFragment<ViewFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_FullPageIssueApp_view on JiraIssueSearchViewMetadata {
				...main_issueNavigator_IssueAppWithData_view
				fieldSets(
					first: $amountOfColumns
					filter: { fieldSetSelectedState: SELECTED }
					scope: $fieldSetsScope
				) {
					edges {
						node {
							fieldSetId
						}
					}
				}
			}
		`,
		view,
	);

	const fieldSetIds = useMemo(
		() => viewData?.fieldSets?.edges?.map((edge) => edge?.node?.fieldSetId).filter(Boolean) ?? [],
		[viewData?.fieldSets?.edges],
	);
	const { hasPrevious, isLoadingPrevious, onLoadPrevious, hasNext, isLoadingNext, onLoadNext } =
		useInfiniteScroll({ fieldSetIds, view: null });

	const issueKeys = useMemo(
		() => issueResultsData?.edges?.map((edge) => edge?.node?.key).filter(Boolean),
		[issueResultsData?.edges],
	);

	const prevIssueKeysLength = usePrevious(issueKeys?.length);

	if (issueKeys && prevIssueKeysLength !== issueKeys.length) {
		mergeFlatListIssues(issueKeys);
	}

	const selectedIssueKey = useSelectedIssueKey();
	const edges = issueResultsData?.edges ?? [];
	const isFirstIssueSelected = edges[0]?.node?.key === selectedIssueKey;
	const isLastIssueSelected = edges[edges.length - 1]?.node?.key === selectedIssueKey;

	// Automatically fetch previous and next pages while navigating issues in full page mode

	useEffect(() => {
		if (isFirstIssueSelected && hasPrevious && !isLoadingPrevious) {
			onLoadPrevious?.();
		}
	}, [hasPrevious, isFirstIssueSelected, isLoadingPrevious, onLoadPrevious]);

	useEffect(() => {
		if (isLastIssueSelected && hasNext && !isLoadingNext) {
			onLoadNext?.();
		}
	}, [hasNext, isLastIssueSelected, isLoadingNext, onLoadNext]);

	const attributes: { edition: ApplicationEdition } | undefined =
		useAnalyticsAttributes(projectType);

	let backButtonUrl: string | undefined;
	if (fg('empanada_nin_migrate_full_page_issue_nav')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		backButtonUrl = useMemo(() => {
			// Not using browser parsing API because it changes spaces to `+`
			const params = parse(routeSearch);
			delete params.selectedIssue;

			return `${routePathname}?${stringify(params)}`;
		}, [routeSearch, routePathname]);
	}

	return (
		<UFOLabel name="full-page-issue-app">
			<ContextualAnalyticsData
				sourceName={ANALYTICS_LIST_VIEW_SOURCE_NAME}
				sourceType={SCREEN}
				{...(attributes ? { attributes } : {})}
			>
				<Shortcuts
					keyMap={{
						z: {
							callback: onBackToSearchByKeyboardShortcut,
						},
						j: {
							callback: onNextIssue,
						},
						k: {
							callback: onPreviousIssue,
						},
					}}
				/>
				<IssueApp
					isEmbedView={false}
					extraHeaderActions={backToSearch}
					issueResults={issueResultsData ?? null}
					view={viewData ?? null}
					backButtonUrl={backButtonUrl}
					routePathname={routePathname}
					routeSearch={routeSearch}
					{...(fg('nin_fix_document_title_in_detail_view') && {
						analyticsSource: ANALYTICS_SOURCE,
					})}
					issueAppEntryPoint={
						fg('nin_issue_app_conditional_entrypoint') ? issueAppEntryPoint : undefined
					}
				/>
			</ContextualAnalyticsData>
		</UFOLabel>
	);
};

const MemoizedFullPageIssueApp = memo(FullPageIssueApp);

const FullPageIssueAppWithRouter = (props: Omit<Props, 'routePathname' | 'routeSearch'>) => {
	return (
		<RouterSubscriber>
			{({ location }) => {
				return (
					<MemoizedFullPageIssueApp
						{...props}
						routePathname={location.pathname}
						routeSearch={location.search}
					/>
				);
			}}
		</RouterSubscriber>
	);
};

export default FullPageIssueAppWithRouter;
